import {
  BKJButton,
  BKJComboBox,
  BKJIcon,
  BKJTextInput,
  BKJToggleInput,
  Flex,
} from '@bookjane2/bookjane-design-library';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import { getPrettyDayAndDateFromDate } from 'components/BKJDatePicker/src/BKJDatePicker.utils';
import { BKJFieldSet, BKJForm, BKJFieldErrorMessage } from 'components/FormWrapper';
import { ShiftDateCalendarInput } from 'components/ShiftDateCalendarInput';
import { H3, P } from 'components/Typography';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { UseFormBehaviorsEventType } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useTranslation } from 'hooks/useTranslation';
import { DateTime } from 'luxon';
import { ApplyAvailabilityToOtherLocationsModal } from 'pages/Settings/pages/LocationSettings/ApplyAvailabilityToOtherLocationsModal';
import { AvailabilityLocationSettingsPageDataProvider } from 'pages/Settings/pages/LocationSettings/AvailabilityLocationSettingsPage.data.context';
import { availabilityDurationOptions } from 'pages/Settings/pages/LocationSettings/AvailabilityLocationSettingsPageForm.constants';
import {
  AvailabilityLocationSettingsPageFormProvider,
  useAvailabilityLocationSettingsPageFormContext,
} from 'pages/Settings/pages/LocationSettings/AvailabilityLocationSettingsPageForm.form.context';
import {
  ReadViewTableCell,
  ReadViewTableWrapper,
} from 'pages/Settings/pages/LocationSettings/AvailabilityLocationSettingsPageForm.styled';
import { useLocationRecordDetailDataContext } from 'pages/Settings/pages/LocationSettings/LocationRecordDetail.data.context';
import { BKJFormLabel } from 'pages/Settings/SettingsPage.styled';
import pluralize from 'pluralize';
import { FC, FormEvent, Fragment, useRef, useState } from 'react';
import { Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';

const AVAILABILITY_DEADLINE_ERROR_ID = 'availabilityDeadlineError';

const InnerAvailabilityForm: FC = () => {
  const { values, onChange, apiErrors, fieldErrors } =
    useAvailabilityLocationSettingsPageFormContext();

  const { t } = useTranslation();
  const deadlineError = apiErrors?.error_messages?.deadline;

  return (
    <Fragment>
      <BKJFieldSet width="414px" maxWidth="414px" gap="14px">
        <ShiftDateCalendarInput
          dropdownVariant="Square"
          label={t('location_settings:START_DATE')}
          name="start_at"
          value={values.start_at}
          error={fieldErrors.start_at || apiErrors?.error_messages?.end_at}
          onChange={onChange}
          isEventValueNormalized
          iconComponent={BKJIcon}
          iconProps={{
            iconName: 'ShiftsDate',
          }}
          dropdownProps={{
            disabledTo: DateTime.now().minus({ day: 1 }).toString(),
            disabledFrom: DateTime.now().plus({ month: 6 }).toString(),
          }}
        />
        <BKJComboBox
          type="Single"
          action="OpenToggle"
          label={t('location_settings:SELECT_DURATION')}
          required
          name="timespan"
          options={availabilityDurationOptions()}
          onChange={onChange}
          value={values.timespan}
        />
      </BKJFieldSet>
      <Flex flexDirection="column">
        <BKJFormLabel>{t('location_settings:SET_DEADLINE')}</BKJFormLabel>
        <Flex alignItems="center">
          <Flex maxWidth="70px">
            <BKJTextInput
              type="number"
              name="deadline"
              onChange={onChange}
              value={values.deadline}
              error={!!deadlineError}
              aria-invalid={deadlineError ? true : undefined}
              aria-describedby={deadlineError ? AVAILABILITY_DEADLINE_ERROR_ID : undefined}
              min={1}
              max={90}
            />
          </Flex>
          &nbsp;&nbsp;&nbsp;
          <P error={!!deadlineError}>{t('location_settings:DAYS_BEFORE')}</P>
        </Flex>
        {deadlineError && (
          <BKJFieldErrorMessage id={AVAILABILITY_DEADLINE_ERROR_ID}>
            {deadlineError}
          </BKJFieldErrorMessage>
        )}
      </Flex>
      <Flex alignItems="center">
        <BKJFormLabel>{t('location_settings:INCLUDE_MOBILE')}</BKJFormLabel>
        &nbsp;&nbsp;&nbsp;
        <BKJToggleInput
          variant="Switch"
          onChange={onChange}
          name="non_mobile"
          checked={values.non_mobile}
        />
      </Flex>
    </Fragment>
  );
};

const EditView: FC = () => {
  const eventRef = useRef<Nullable<UseFormBehaviorsEventType | UseFormBehaviorsEventType[]>>(null);
  const { data } = useLocationRecordDetailDataContext();
  const [isWarningOpen, setWarningOpen] = useState(false);
  const { onSubmit, values, onChange } = useAvailabilityLocationSettingsPageFormContext();

  const { t } = useTranslation();

  return (
    <Fragment>
      {isWarningOpen && (
        <BKJCenteredDialog
          variant="Danger"
          title={t('location_settings:TURN_OFF')}
          text={t('location_settings:AVAILABILITY_WILL_NO_LONGER_INFO')}
          isOpen={isWarningOpen}
          cancelLabel={t('auth:CANCEL')}
          actionLabel={t('auth:CONFIRM')}
          onAction={() => {
            onSubmit(eventRef.current! as unknown as FormEvent<HTMLFormElement>);
            eventRef.current = null;
          }}
          onClose={() => {
            onChange({ target: { name: 'isAvailabilityEnabled', value: true } });
            setWarningOpen(false);
          }}
          onCancel={() => {
            onChange({ target: { name: 'isAvailabilityEnabled', value: true } });
            setWarningOpen(false);
          }}
        />
      )}
      <BKJForm onSubmit={onSubmit} width="672px" maxWidth="672px" gap="22px">
        <H3>{data.name}</H3>
        <P>{t('location_settings:AVAILABILITY_INFO')}</P>
        <Flex alignItems="center">
          <BKJFormLabel>
            {t('location_settings:TURN_ON_OFF_AVAILABILITY', {
              value:
                values.isAvailabilityEnabled || isWarningOpen
                  ? t('location_settings:OFF')
                  : t('location_settings:ON'),
            })}
          </BKJFormLabel>
          &nbsp;&nbsp;&nbsp;
          <BKJToggleInput
            variant="Switch"
            onChange={(event) => {
              onChange(event);
              if (values.isAvailabilityEnabled) {
                eventRef.current = event;
                setWarningOpen(true);
              }
            }}
            name="isAvailabilityEnabled"
            checked={values.isAvailabilityEnabled || isWarningOpen}
          />
        </Flex>

        {(values.isAvailabilityEnabled || isWarningOpen) && (
          <Fragment>
            <InnerAvailabilityForm />
            <BKJButton variant="PurpleSolid" width="192px" minWidth="192px" type="submit">
              {t('auth:SAVE')}
            </BKJButton>
          </Fragment>
        )}
      </BKJForm>
      <ApplyAvailabilityToOtherLocationsModal />
    </Fragment>
  );
};

const ReadView: FC = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const { values } = useAvailabilityLocationSettingsPageFormContext();
  const { data } = useLocationRecordDetailDataContext();
  const [isWarningOpen, setWarningOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <Fragment>
      {isWarningOpen && (
        <BKJCenteredDialog
          variant="Danger"
          title={t('location_settings:RESET_AVAILABILITY_SETTINGS')}
          text={t('location_settings:RESET_AVAILABILITY_SETTINGS_DESCRIPTION')}
          isOpen={isWarningOpen}
          cancelLabel={t('auth:CANCEL')}
          actionLabel={t('auth:CONFIRM')}
          onAction={() => {
            setWarningOpen(false);
            history.push(`${match.url}/edit`);
          }}
          onClose={() => {
            setWarningOpen(false);
          }}
          onCancel={() => {
            setWarningOpen(false);
          }}
        />
      )}
      <Flex flexDirection="column" gap="22px">
        <Flex justifyContent="space-between">
          <H3>{data.name}</H3>
          <UserGuard
            userType={[UserPermissionEnum.USER_TYPE_AGENCY, UserPermissionEnum.USER_TYPE_B2B]}
          >
            <BKJButton
              variant="PurpleOutlined"
              width="192px"
              minWidth="192px"
              onClick={() => {
                if (values.isAvailabilityEnabled) {
                  setWarningOpen(true);
                } else {
                  history.push(`${match.url}/edit`);
                }
              }}
            >
              {t('location_settings:EDIT')}
            </BKJButton>
          </UserGuard>
        </Flex>
        <P></P>
        <P bold>
          {t('location_settings:AVAILABILITY_INFO')}:&nbsp;
          <P>
            {values.isAvailabilityEnabled ? t('location_settings:ON') : t('location_settings:OFF')}
          </P>
        </P>
        {values.isAvailabilityEnabled && (
          <Fragment>
            <ReadViewTableWrapper>
              <ReadViewTableCell bgColor="Grey1" topBorder>
                {t('location_settings:START_DATE_INFO')}:
              </ReadViewTableCell>
              <ReadViewTableCell bgColor="Grey1" topBorder>
                {getPrettyDayAndDateFromDate(values.start_at, false)}
              </ReadViewTableCell>
              <ReadViewTableCell>{t('location_settings:DURATION_INFO')}:</ReadViewTableCell>
              <ReadViewTableCell>
                {values.timespan}&nbsp;
                {pluralize(t('WEEK'), Number.parseInt(values.timespan[0]))}
              </ReadViewTableCell>
              <ReadViewTableCell bgColor="Grey1">
                {t('location_settings:DEADLINE_INFO')}:
              </ReadViewTableCell>
              <ReadViewTableCell bgColor="Grey1">
                {values.deadline}&nbsp;
                {pluralize(t('location_settings:DAY'), Number.parseInt(values.deadline))}
                &nbsp;{t('location_settings:BEFORE_THE_START')}
              </ReadViewTableCell>
            </ReadViewTableWrapper>
          </Fragment>
        )}
      </Flex>
    </Fragment>
  );
};

const Component: FC = () => {
  const match = useRouteMatch();
  return (
    <Switch>
      <Route exact path={match.path} component={ReadView} />
      <Route path={`${match.path}/edit`} component={EditView} />
    </Switch>
  );
};

export const AvailabilityLocationSettingsPageForm = () => {
  const { locationId } = useParams<{ locationId: string }>();

  return (
    <AvailabilityLocationSettingsPageDataProvider locationId={locationId}>
      <AvailabilityLocationSettingsPageFormProvider>
        <Component />
      </AvailabilityLocationSettingsPageFormProvider>
    </AvailabilityLocationSettingsPageDataProvider>
  );
};
